import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  TextField
} from "@fluentui/react";
import { useFormik } from "formik";
import * as Yup from "yup";

interface CustomizationFormValues {
  monthlyDiscount: string;
  approvalChecked: boolean;
}

interface PersonalizeOfferModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (discount: number) => void;
}

const validationSchema = Yup.object({
  monthlyDiscount: Yup.string().required("Digite o desconto mensal"),
  approvalChecked: Yup.boolean()
});

const initialValues: CustomizationFormValues = {
  monthlyDiscount: "0,00",
  approvalChecked: false
};

const customizationDialogContentProps = {
  type: DialogType.normal,
  title: "Personalize o Plano de Benefícios"
};

const formatCurrency = (value: string): string => {
  const numericValue = value.replace(/\D/g, "");
  const cents = parseInt(numericValue || "0", 10);
  const decimalValue = (cents / 100).toFixed(2);

  return decimalValue.replace(".", ",");
};

export function PersonalizeOfferModal({
  isOpen,
  onDismiss,
  onSave
}: PersonalizeOfferModalProps): JSX.Element {
  const formik = useFormik<CustomizationFormValues>({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const numericValue = Number(values.monthlyDiscount.replace(",", "."));
      onSave(numericValue);
      resetForm();
    }
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    resetForm
  } = formik;

  const handleCancel = (): void => {
    onDismiss();
    resetForm();
  };

  const getFieldError = (
    fieldName: keyof CustomizationFormValues
  ): string | undefined => {
    return touched[fieldName] && errors[fieldName]
      ? errors[fieldName]
      : undefined;
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleCancel}
      dialogContentProps={customizationDialogContentProps}
      minWidth={500}
    >
      <form onSubmit={handleSubmit}>
        <TextField
          id="monthly-discount"
          label="Desconto Mensal no Plano de Benefícios"
          placeholder="0,00"
          prefix="R$"
          required
          value={values.monthlyDiscount}
          onChange={(_, newValue) => {
            const formatted = formatCurrency(newValue ?? "");
            void setFieldValue("monthlyDiscount", formatted);
          }}
          onBlur={() => void setFieldTouched("monthlyDiscount", true)}
          errorMessage={getFieldError("monthlyDiscount")}
          styles={{ root: { marginBottom: 20 } }}
        />

        <Checkbox
          label="A personalização deste benefício estará sujeita à aprovação posterior do contrato."
          checked={values.approvalChecked}
          onChange={(_, checked) =>
            void setFieldValue("approvalChecked", checked ?? false)
          }
          styles={{ root: { marginBottom: 20 } }}
        />

        <DialogFooter>
          <DefaultButton onClick={handleCancel} text="Cancelar" />
          <PrimaryButton
            type="submit"
            text="Salvar personalização"
            disabled={!values.approvalChecked}
          />
        </DialogFooter>
      </form>
    </Dialog>
  );
}
